import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    CalendarDays, CalendarX2, Check, ChevronDown, ChevronLeft, ChevronRight,
    Clock3, Hash, PartyPopper, Phone, Search, ShieldCheck, Store, UserRound,
    UsersRound, X, XCircle,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import DatePicker from '../../components/DatePicker';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { formatBookingDates } from '../../utils/bookingDates';
import './Bookings.css';

const STATUS_META = {
    Pending: { label: 'Pending', className: 'pending', Icon: Clock3 },
    Confirmed: { label: 'Confirmed', className: 'confirmed', Icon: Check },
    Cancelled: { label: 'Cancelled', className: 'cancelled', Icon: X },
};

const PROVIDER_PAGE_SIZE = 20;
const BOOKINGS_PAGE_SIZE = 10;

const TABLE_COLUMNS = [
    { label: '#', Icon: Hash, className: 'bookings-col-index' },
    { label: 'Customer', Icon: UserRound },
    { label: 'Service', Icon: Store },
    { label: 'Event Type', Icon: PartyPopper },
    { label: 'Event Date', Icon: CalendarDays },
    { label: 'Guests', Icon: UsersRound },
    { label: 'Status', Icon: ShieldCheck },
    { label: 'Actions', Icon: null },
];

const Bookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [providerId, setProviderId] = useState('');
    const [providerQuery, setProviderQuery] = useState('');
    const [providerOpen, setProviderOpen] = useState(false);
    const [providerVisible, setProviderVisible] = useState(PROVIDER_PAGE_SIZE);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const providerRef = useRef(null);

    useEffect(() => { fetchBookings(); }, []);

    // Fresh 20 whenever the dropdown is (re)opened or the search narrows the list.
    useEffect(() => { setProviderVisible(PROVIDER_PAGE_SIZE); }, [providerOpen, providerQuery]);

    useEffect(() => { setPage(1); }, [filter, providerId, startDate, endDate]);

    useEffect(() => {
        if (!providerOpen) return undefined;
        const onDocClick = e => { if (providerRef.current && !providerRef.current.contains(e.target)) setProviderOpen(false); };
        const onKey = e => { if (e.key === 'Escape') setProviderOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [providerOpen]);

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const res = await API.get('/admin/bookings');
            setBookings(res.data.bookings);
        } catch (err) {
            toast.error('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    };

    const handleStatus = async (id, status) => {
        try {
            await API.put(`/admin/bookings/${id}/status`, { status });
            toast.success(`Booking ${status}!`);
            fetchBookings();
        } catch (err) {
            toast.error('Failed!');
        }
    };

    // Every booking already carries its populated service (the provider's
    // listing), so the dropdown options come straight from what's loaded —
    // no separate providers fetch, and the ids line up with booking.service._id.
    const providers = useMemo(() => {
        const byId = new Map();
        bookings.forEach(b => { if (b.service?._id) byId.set(b.service._id, b.service); });
        return [...byId.values()].sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));
    }, [bookings]);

    const filteredProviders = useMemo(() => {
        const query = providerQuery.trim().toLowerCase();
        if (!query) return providers;
        return providers.filter(p => [p.businessName, p.category, p.city].some(v => String(v || '').toLowerCase().includes(query)));
    }, [providers, providerQuery]);

    const visibleProviders = filteredProviders.slice(0, providerVisible);
    const moreProvidersCount = filteredProviders.length - visibleProviders.length;

    const selectedProvider = providers.find(p => p._id === providerId) || null;

    const providerScoped = providerId ? bookings.filter(b => b.service?._id === providerId) : bookings;

    const inDateRange = b => {
        if (!startDate && !endDate) return true;
        const eventDate = new Date(b.eventDate);
        if (startDate && eventDate < new Date(`${startDate}T00:00:00`)) return false;
        if (endDate && eventDate > new Date(`${endDate}T23:59:59`)) return false;
        return true;
    };
    const dateScoped = (startDate || endDate) ? providerScoped.filter(inDateRange) : providerScoped;

    const filtered = filter === 'all' ? dateScoped : dateScoped.filter(b => b.status === filter);

    const totalPages = Math.max(1, Math.ceil(filtered.length / BOOKINGS_PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * BOOKINGS_PAGE_SIZE;
    const paginatedBookings = filtered.slice(pageStart, pageStart + BOOKINGS_PAGE_SIZE);

    const stats = [
        { label: 'Total bookings', value: dateScoped.length, Icon: CalendarDays, tone: 'primary' },
        { label: 'Pending', value: dateScoped.filter(b => b.status === 'Pending').length, Icon: Clock3, tone: 'amber' },
        { label: 'Confirmed', value: dateScoped.filter(b => b.status === 'Confirmed').length, Icon: Check, tone: 'green' },
        { label: 'Cancelled', value: dateScoped.filter(b => b.status === 'Cancelled').length, Icon: X, tone: 'rose' },
    ];

    const filters = [
        { id: 'all', label: 'All', count: dateScoped.length },
        { id: 'Pending', label: 'Pending', count: dateScoped.filter(b => b.status === 'Pending').length },
        { id: 'Confirmed', label: 'Confirmed', count: dateScoped.filter(b => b.status === 'Confirmed').length },
        { id: 'Cancelled', label: 'Cancelled', count: dateScoped.filter(b => b.status === 'Cancelled').length },
    ];

    const selectProvider = id => { setProviderId(id); setProviderOpen(false); setProviderQuery(''); };
    const clearDateRange = () => { setStartDate(''); setEndDate(''); };

    return (
        <div className="bookings-layout">
            <Sidebar />
            <main className="admin-content bookings-content">
                <Navbar title={<span className="bookings-title"><CalendarDays size={19} /> Bookings</span>} />
                <div className="bookings-page">
                    <div className="bookings-heading">
                        <div>
                            <h2>Manage bookings</h2>
                            <p>Review service bookings and update their status.</p>
                        </div>
                        <span className="bookings-total"><CalendarDays size={15} /> {dateScoped.length} bookings{selectedProvider ? ` · ${selectedProvider.businessName}` : ''}</span>
                    </div>

                    {!providerId && (
                        <section className="bookings-stats" aria-label="Booking overview">
                            {stats.map(({ label, value, Icon, tone }) => (
                                <article className="bookings-stat-card" key={label}>
                                    <span className={`bookings-stat-icon ${tone}`}><Icon size={19} /></span>
                                    <div><strong>{value}</strong><span>{label}</span></div>
                                </article>
                            ))}
                        </section>
                    )}

                    <section className="bookings-panel">
                        <div className="bookings-toolbar">
                            <div className="bookings-provider-select" ref={providerRef}>
                                <button
                                    type="button"
                                    className={`bookings-provider-trigger ${providerOpen ? 'is-open' : ''}`}
                                    onClick={() => setProviderOpen(v => !v)}
                                    aria-haspopup="listbox"
                                    aria-expanded={providerOpen}
                                >
                                    <Store size={15} />
                                    <span className={selectedProvider ? 'bookings-provider-value' : 'bookings-provider-placeholder'}>
                                        {selectedProvider ? selectedProvider.businessName : 'All service providers'}
                                    </span>
                                    <ChevronDown size={14} className="bookings-provider-chevron" />
                                </button>
                                {providerOpen && (
                                    <div className="bookings-provider-menu" role="listbox">
                                        <label className="bookings-provider-search">
                                            <Search size={14} />
                                            <input
                                                type="search"
                                                placeholder="Search service provider..."
                                                value={providerQuery}
                                                onChange={e => setProviderQuery(e.target.value)}
                                                autoFocus
                                            />
                                        </label>
                                        <div className="bookings-provider-options">
                                            <button
                                                type="button"
                                                className={`bookings-provider-option ${!providerId ? 'is-selected' : ''}`}
                                                onClick={() => selectProvider('')}
                                            >
                                                <span className="bookings-provider-option-name">All service providers</span>
                                            </button>
                                            {visibleProviders.map(p => (
                                                <button
                                                    type="button"
                                                    key={p._id}
                                                    className={`bookings-provider-option ${providerId === p._id ? 'is-selected' : ''}`}
                                                    onClick={() => selectProvider(p._id)}
                                                >
                                                    {providerId === p._id && <Check size={13} className="bookings-provider-option-check" />}
                                                    <span className="bookings-provider-option-name">{p.businessName}</span>
                                                    <span className="bookings-provider-option-meta">{[p.category, p.city].filter(Boolean).join(' · ')}</span>
                                                </button>
                                            ))}
                                            {filteredProviders.length === 0 && (
                                                <div className="bookings-provider-empty">No matching providers</div>
                                            )}
                                            {moreProvidersCount > 0 && (
                                                <button
                                                    type="button"
                                                    className="bookings-provider-more"
                                                    onClick={() => setProviderVisible(v => v + PROVIDER_PAGE_SIZE)}
                                                >
                                                    Load 20 more ({moreProvidersCount} remaining)
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="bookings-date-range" aria-label="Filter bookings by event date range">
                                <div className="bookings-date-field">
                                    <DatePicker value={startDate} onChange={setStartDate} placeholder="Start date" />
                                </div>
                                <span className="bookings-date-sep">to</span>
                                <div className="bookings-date-field">
                                    <DatePicker value={endDate} onChange={setEndDate} placeholder="End date" />
                                </div>
                                {(startDate || endDate) && (
                                    <button type="button" className="bookings-date-clear" onClick={clearDateRange} title="Clear date range" aria-label="Clear date range">
                                        <XCircle size={16} />
                                    </button>
                                )}
                            </div>
                            <div className="bookings-filters" aria-label="Filter bookings by status">
                                {filters.map(option => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={filter === option.id ? 'active' : ''}
                                        onClick={() => setFilter(option.id)}
                                    >
                                        {option.label}<span>{option.count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {!providerId ? (
                            <div className="bookings-empty">
                                <Store size={24} />
                                <strong>Select a service provider</strong>
                                <span>Choose a provider from the dropdown above to view their bookings.</span>
                            </div>
                        ) : loading ? (
                            <div className="bookings-state"><BrandLoader size={44} />Loading bookings...</div>
                        ) : filtered.length === 0 ? (
                            <div className="bookings-empty">
                                <CalendarX2 size={24} />
                                <strong>No bookings found</strong>
                                <span>Try a different status filter or date range.</span>
                            </div>
                        ) : (
                            <>
                            <div className="bookings-table-scroll">
                                <table className="bookings-table">
                                    <thead>
                                        <tr>
                                            {TABLE_COLUMNS.map(({ label, Icon, className }) => (
                                                <th key={label} className={className || ''}>
                                                    <span>{Icon && <Icon size={14} />}{label}</span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedBookings.map((b, i) => {
                                            const meta = STATUS_META[b.status] || STATUS_META.Pending;
                                            const StatusIcon = meta.Icon;
                                            return (
                                                <tr key={b._id}>
                                                    <td className="bookings-row-number">{pageStart + i + 1}</td>
                                                    <td>
                                                        <div className="bookings-person">
                                                            <span className="bookings-avatar">{(b.user?.name || '?').charAt(0).toUpperCase()}</span>
                                                            <div>
                                                                <strong>{b.user?.name}</strong>
                                                                <small><Phone size={11} />{b.user?.mobile}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="bookings-multi-value">
                                                            <span>{b.service?.businessName}</span>
                                                            <span>{b.service?.category}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="bookings-cell-with-icon"><PartyPopper size={14} />{b.eventType}</span>
                                                    </td>
                                                    <td>
                                                        <span className="bookings-cell-with-icon"><CalendarDays size={14} />{formatBookingDates(b, false)}</span>
                                                    </td>
                                                    <td>
                                                        <span className="bookings-cell-with-icon"><UsersRound size={14} />{b.guestCount || '-'}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`bookings-badge status ${meta.className}`}><StatusIcon size={13} />{meta.label}</span>
                                                    </td>
                                                    <td>
                                                        <div className="bookings-action-group">
                                                            {b.status === 'Pending' && (
                                                                <button
                                                                    type="button"
                                                                    className="bookings-action success"
                                                                    onClick={() => handleStatus(b._id, 'Confirmed')}
                                                                    title="Confirm booking"
                                                                    aria-label="Confirm booking"
                                                                ><Check size={15} /></button>
                                                            )}
                                                            {b.status !== 'Cancelled' && (
                                                                <button
                                                                    type="button"
                                                                    className="bookings-action danger"
                                                                    onClick={() => handleStatus(b._id, 'Cancelled')}
                                                                    title="Cancel booking"
                                                                    aria-label="Cancel booking"
                                                                ><X size={15} /></button>
                                                            )}
                                                            {b.status === 'Cancelled' && <span className="bookings-muted">-</span>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <footer className="bookings-pagination">
                                <span>Showing {pageStart + 1}-{Math.min(pageStart + BOOKINGS_PAGE_SIZE, filtered.length)} of {filtered.length} bookings</span>
                                <div>
                                    <button type="button" onClick={() => setPage(value => Math.max(1, value - 1))} disabled={currentPage === 1} aria-label="Previous page"><ChevronLeft size={16} /></button>
                                    <span>Page <strong>{currentPage}</strong> of {totalPages}</span>
                                    <button type="button" onClick={() => setPage(value => Math.min(totalPages, value + 1))} disabled={currentPage === totalPages} aria-label="Next page"><ChevronRight size={16} /></button>
                                </div>
                            </footer>
                            </>
                        )}
                    </section>
                </div>
            </main>
        </div>
    );
};

export default Bookings;
